import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { CreateProductDTO, ProductResponseDTO, UpdateProductDTO } from '@libs/dtos/product/product.dto';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { PlatformService } from '@services/platforms/platform.service';
import { ProductService } from '@services/products/product.service';
import { ProviderService } from '@services/provider/provider.service';
import { ValidationService } from '@services/ui/validation.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ToggleSwitchComponent } from '@shared/components/toggle-switch/toggle-switch.component';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';
import { catchError, finalize, of } from 'rxjs';
import {
	firstError,
	maxNotBelowMinValidator,
	optionalPositiveInt,
	requiredPositiveInt,
	uniqueProviderCodeValidator,
} from '../product-form.rules';

@Component({
	selector: 'app-product-crud',
	standalone: true,
	imports: [SharedModule, ButtonComponent, InputComponent, SelectComponent, ToggleSwitchComponent],
	templateUrl: './product-crud.component.html',
	styleUrl: './product-crud.component.scss',
})
export class ProductCrudComponent implements OnInit {
	@Output() productUpdated = new EventEmitter<{ product: ProductResponseDTO; created: boolean }>();
	// Emitted only on a successful save, with the saved product

	visible = false;
	isEditing = false;
	currentProductId: string | null = null;
	dialogTitle = 'Nuevo Producto';
	actionButtonLabel = 'Añadir Producto';
	isLoading = false;
	productForm: FormGroup;
	fetchingService: boolean[] = [];

	platformOptions: { label: string; value: string }[] = [];
	providerOptions: { label: string; value: string }[] = [];

	constructor(
		private fb: FormBuilder,
		private productService: ProductService,
		private platformService: PlatformService,
		private providerService: ProviderService,
		private validationService: ValidationService,
		private messageAlertService: MessageAlertService,
		private cdr: ChangeDetectorRef,
		private translocoService: TranslocoService,
	) {
		this.productForm = this.fb.group(
			{
				name: ['', [Validators.required]],
				requiresCustomComments: [false],
				// FormArray of quantity-price pairs
				priceTiers: this.fb.array([]),
				// Optional, whole numbers >= 1 (and max >= min, group validator)
				minAmount: [null, optionalPositiveInt],
				maxAmount: [null, optionalPositiveInt],
				averageTime: [null, optionalPositiveInt],
				platformId: ['', [Validators.required]],
				// FormArray of providers (several allowed, never the same provider + code twice)
				providers: this.fb.array([], [uniqueProviderCodeValidator()]),
			},
			{ validators: [maxNotBelowMinValidator()] },
		);
	}

	// Getter for the providers FormArray
	get providersArray(): FormArray {
		return this.productForm.get('providers') as FormArray;
	}

	// Getter for the quantity-price pairs FormArray
	get priceTiersArray(): FormArray {
		return this.productForm.get('priceTiers') as FormArray;
	}

	// Build a FormGroup for a quantity-price pair
	private createPriceTierGroup(amount: number | null = null, price: number | null = null): FormGroup {
		return this.fb.group({
			amount: [amount, requiredPositiveInt],
			price: [price, [Validators.required, Validators.min(0.01)]],
		});
	}

	// Add a new quantity-price pair
	addPriceTier(amount: number | null = null, price: number | null = null): void {
		this.priceTiersArray.push(this.createPriceTierGroup(amount, price));
	}

	// Remove a quantity-price pair
	removePriceTier(index: number): void {
		if (this.priceTiersArray.length > 1) {
			this.priceTiersArray.removeAt(index);
		}
	}

	ngOnInit() {
		this.loadPlatforms();
	}

	show(product?: ProductResponseDTO): void {
		this.visible = true;
		// Providers only matter once the form opens (shared and cached by ProviderService)
		this.loadProviders();
		this.isEditing = !!product;
		this.currentProductId = product?.id?.toString() || null;
		this.dialogTitle = this.translocoService.translate(
			this.isEditing ? 'productsCrud.dialogTitleFull.edit' : 'productsCrud.dialogTitleFull.new',
		);
		this.actionButtonLabel = this.translocoService.translate(
			this.isEditing ? 'productsCrud.actionButton.edit' : 'productsCrud.actionButton.new',
		);

		// Clear the FormArrays (and the per-provider fetch spinners that index them)
		this.providersArray.clear();
		this.priceTiersArray.clear();
		this.fetchingService = [];

		if (this.isEditing && product) {
			this.productForm.patchValue({
				name: product.name,
				requiresCustomComments: product.requiresCustomComments || false,
				minAmount: product.minAmount || null,
				maxAmount: product.maxAmount || null,
				averageTime: product.averageTime || null,
				platformId: product.platformId.toString(),
			});

			// Add the existing quantity-price pairs
			if (product.amounts && product.prices) {
				const amounts = Array.isArray(product.amounts) ? product.amounts : [];
				const prices = Array.isArray(product.prices) ? product.prices : [];
				const length = Math.min(amounts.length, prices.length);
				for (let i = 0; i < length; i++) {
					this.addPriceTier(amounts[i], prices[i]);
				}
			}

			// Add the existing providers to the FormArray
			if (product.providers && product.providers.length > 0) {
				product.providers.forEach((provider) => {
					this.addProvider(provider.providerId.toString(), provider.code, provider.isActive, provider.id);
				});
			}
		} else {
			this.productForm.reset();
			// Add an empty quantity-price pair by default
			this.addPriceTier();
			// Add an empty provider by default (active)
			this.addProvider('', '', true);
		}
	}

	// Build a FormGroup for a provider
	private createProviderGroup(providerId = '', code = '', isActive = false, id?: number): FormGroup {
		return this.fb.group({
			id: [id || null],
			providerId: [providerId, [Validators.required]],
			code: [code, [Validators.required]],
			isActive: [isActive],
		});
	}

	// Add a new provider to the array
	addProvider(providerId = '', code = '', isActive = false, id?: number): void {
		this.providersArray.push(this.createProviderGroup(providerId, code, isActive, id));
		this.fetchingService.push(false);
	}

	// Remove a provider from the array
	removeProvider(index: number): void {
		if (this.providersArray.length > 1) {
			this.providersArray.removeAt(index);
			this.fetchingService.splice(index, 1);
			// If the active one is removed, activate the first one
			const hasActive = this.providersArray.controls.some((ctrl) => ctrl.get('isActive')?.value);
			if (!hasActive && this.providersArray.length > 0) {
				this.providersArray.at(0).get('isActive')?.setValue(true);
			}
		}
	}

	canFetchService(index: number): boolean {
		const g = this.providersArray.at(index);
		return !!g.get('providerId')?.value && !!g.get('code')?.value;
	}

	fetchProviderService(index: number): void {
		const g = this.providersArray.at(index);
		const providerId = Number(g.get('providerId')?.value);
		const code = g.get('code')?.value;

		if (!providerId || !code) return;

		this.fetchingService[index] = true;

		this.providerService.getServiceByCode(providerId, code).subscribe({
			next: (service) => {
				this.fetchingService[index] = false;
				if (!service) {
					this.messageAlertService.showMessage(
						'error',
						this.translocoService.translate('common.labels.error'),
						this.translocoService.translate('productsCrud.messages.serviceNotFound'),
					);
					return;
				}
				this.productForm.patchValue({
					name: service.name,
					minAmount: service.min,
					maxAmount: service.max,
					requiresCustomComments: service.type === 'Custom Comments',
				});
			},
			error: () => {
				this.fetchingService[index] = false;
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					this.translocoService.translate('productsCrud.messages.serviceInfoError'),
				);
			},
		});
	}

	// Set a provider as active (only one can be active)
	setActiveProvider(index: number): void {
		this.providersArray.controls.forEach((ctrl, i) => {
			ctrl.get('isActive')?.setValue(i === index);
		});
	}

	onSubmit(): void {
		if (this.isLoading) {
			return;
		}

		if (this.productForm.invalid) {
			this.productForm.markAllAsTouched();
			return;
		}

		// Check there is at least one quantity-price pair
		if (this.priceTiersArray.length === 0) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('productsCrud.messages.atLeastOnePriceTier'),
			);
			return;
		}

		this.isLoading = true;
		const formData = this.productForm.value;

		// Extract amounts and prices from the FormArray
		const amounts: number[] = [];
		const prices: number[] = [];

		for (const tier of formData.priceTiers) {
			if (tier.amount && tier.price) {
				amounts.push(Number(tier.amount));
				prices.push(Number(tier.price));
			}
		}

		// Validate no invalid values
		if (amounts.length === 0 || prices.length === 0) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('productsCrud.messages.atLeastOnePriceTierValid'),
			);
			this.isLoading = false;
			return;
		}

		if (amounts.some((x: number) => x <= 0) || prices.some((x: number) => x <= 0)) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('productsCrud.messages.valuesGreaterThanZero'),
			);
			this.isLoading = false;
			return;
		}

		// Check that at least one provider is active
		const providers = formData.providers.map((p: any) => ({
			id: p.id || undefined,
			providerId: p.providerId,
			code: p.code,
			isActive: p.isActive,
		}));

		const activeProviders = providers.filter((p: any) => p.isActive);
		if (activeProviders.length !== 1) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('productsCrud.messages.oneActiveProvider'),
			);
			this.isLoading = false;
			return;
		}

		const data = {
			name: formData.name,
			requiresCustomComments: formData.requiresCustomComments || false,
			amounts,
			prices,
			platformId: formData.platformId,
			providers,
			minAmount: formData.minAmount,
			maxAmount: formData.maxAmount,
			averageTime: formData.averageTime,
		};

		const request$ =
			this.isEditing && this.currentProductId
				? this.productService.updateProduct(this.currentProductId, data as UpdateProductDTO)
				: this.productService.createProduct(data as CreateProductDTO);

		// Keep the dialog open until the server answers: on error the typed values stay
		request$
			.pipe(
				catchError((err) => {
					// errors.<code> from the backend; generic key as fallback
					const errorMessage = translateApiError(
						err,
						this.translocoService,
						this.isEditing ? 'productsCrud.messages.updateError' : 'productsCrud.messages.createError',
					);

					this.messageAlertService.showMessage(
						'error',
						this.translocoService.translate('common.labels.error'),
						errorMessage,
					);

					return of(null);
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((response) => {
				if (response) {
					this.visible = false;
					this.productUpdated.emit({ product: response, created: !this.isEditing });
					this.messageAlertService.showMessage(
						'success',
						this.translocoService.translate('common.labels.success'),
						this.translocoService.translate(
							this.isEditing
								? 'productsCrud.messages.updateSuccess'
								: 'productsCrud.messages.createSuccess',
						),
					);
				}
			});
	}

	getErrorMessage(field: string): string | null {
		const control = this.productForm?.get(field);
		const error =
			firstError(control?.errors) ?? (field === 'maxAmount' ? firstError(this.productForm.errors) : null);
		return error ? this.validationService.getValidatorErrorMessage(error.key, error.params) : null;
	}

	/** Touched and invalid; max also when it is below min (a form-level error) */
	isFieldInvalid(field: string): boolean {
		const control = this.productForm.get(field);
		if (!control?.touched) return false;
		return control.invalid || (field === 'maxAmount' && this.productForm.hasError('maxBelowMin'));
	}

	/** Error of one control of a price tier / provider row (shown once the row was touched) */
	rowError(array: FormArray, index: number, field: string): string {
		const control = array.at(index)?.get(field);
		if (!control?.touched) return '';
		const error = firstError(control.errors);
		if (error) return this.validationService.getValidatorErrorMessage(error.key, error.params);
		if (field === 'code' && this.isDuplicateProvider(index)) {
			return this.validationService.getValidatorErrorMessage('duplicateProvider');
		}
		return '';
	}

	/** Row uses the same provider + code as another row */
	isDuplicateProvider(index: number): boolean {
		const indexes: number[] = this.providersArray.errors?.['duplicateProvider']?.indexes ?? [];
		return indexes.includes(index);
	}

	private loadPlatforms(): void {
		this.isLoading = true;

		this.platformService
			.getAllPlatforms()
			.pipe(
				finalize(() => {
					this.isLoading = false; // also on error: no stuck spinner
				}),
			)
			.subscribe({
				next: (platforms) => {
					this.platformOptions = platforms.map((platform) => ({
						label: platform.name,
						value: platform.id.toString(),
					}));
				},
				error: () => this.notifyOptionsLoadError(),
			});
	}

	private loadProviders(): void {
		this.providerService.getProviders().subscribe({
			next: (response) => {
				this.providerOptions = response.data.map((provider) => ({
					label: provider.name,
					value: provider.id.toString(),
				}));
			},
			error: () => this.notifyOptionsLoadError(),
		});
	}

	/** The form cannot be filled without its options: tell the user instead of leaving empty selects */
	private notifyOptionsLoadError(): void {
		this.messageAlertService.showMessage(
			'error',
			this.translocoService.translate('common.labels.error'),
			this.translocoService.translate('productsCrud.messages.optionsLoadError'),
		);
	}

	submitOnEnter(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.onSubmit();
		}
	}

	submitOnEnterSafe(event: Event): void {
		this.submitOnEnter(event as KeyboardEvent);
	}
}
